import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { admin } from "../../api/client";
import { ORDER_STATES } from "../../api/types";
import { TocaPage } from "../../components/Shell";
import { Empty, ErrorBox, Spinner, StatePill } from "../../components/ui";
import { useAsync } from "../../hooks/useAsync";
import { fmt, stateLabel, t } from "../../i18n";
import { dateTimeShort } from "../../lib/format";
import { brl, usd } from "../../lib/money";
import { supplierName } from "../Painel";

export function TocaPedidos() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const state = sp.get("state") ?? "";
  const companyId = sp.get("companyId") ?? "";
  const exception = sp.get("exception") === "true";
  const page = Number(sp.get("page") ?? "1") || 1;
  const [companyDraft, setCompanyDraft] = useState(companyId);
  const { data, error, loading, reload } = useAsync(() => admin.orders({ state: state || undefined, companyId: companyId || undefined, exception: exception || undefined, page }), [state, companyId, exception, page]);
  const items = data?.items ?? [];

  function set(k: string, v: string) {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v);
    else n.delete(k);
    n.delete("page");
    setSp(n);
  }

  return (
    <TocaPage title={t.toca.ordersTitle}>
      <div className="card pad">
        <div className="row" style={{ alignItems: "flex-end", gap: 12 }}>
          <div className="field">
            <label htmlFor="f-state">{t.toca.filterState}</label>
            <div className="inp" style={{ minHeight: 40 }}>
              <select id="f-state" value={state} onChange={(e) => set("state", e.target.value)}>
                <option value="">{t.common.all}</option>
                {ORDER_STATES.map((s) => <option key={s} value={s}>{stateLabel(s)}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label htmlFor="f-company">{t.toca.filterCompany}</label>
            <div className="inp" style={{ minHeight: 40 }}>
              <input id="f-company" className="mono" value={companyDraft} onChange={(e) => setCompanyDraft(e.target.value)} onBlur={() => set("companyId", companyDraft.trim())} onKeyDown={(e) => e.key === "Enter" && set("companyId", companyDraft.trim())} />
            </div>
          </div>
          <label className="check" style={{ minHeight: 40, alignItems: "center" }}>
            <input type="checkbox" checked={exception} onChange={(e) => set("exception", e.target.checked ? "true" : "")} />
            <span>{t.toca.filterException}</span>
          </label>
          <span className="muted small" style={{ marginLeft: "auto" }}>{data ? fmt(t.common.nRecords, { n: data.total }) : ""}</span>
        </div>
      </div>
      <div className="card">
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <div style={{ padding: 16 }}><ErrorBox error={error} onRetry={reload} /></div>
        ) : items.length === 0 ? (
          <Empty body={t.toca.ordersEmpty} />
        ) : (
          <div className="tscroll" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th>{t.toca.queueColOrder}</th>
                  <th>{t.toca.company}</th>
                  <th>{t.suppliers.colName}</th>
                  <th>{t.panel.colUsd}</th>
                  <th>{t.panel.colBrl}</th>
                  <th>{t.common.status}</th>
                  <th>{t.common.date}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((o) => (
                  <tr key={o.id} className="link" onClick={() => navigate(`/toca/pedidos/${o.id}`)}>
                    <td className="mono" style={{ fontSize: 13 }}><Link to={`/toca/pedidos/${o.id}`} style={{ color: "inherit", textDecoration: "none" }} onClick={(e) => e.stopPropagation()}>{o.id}</Link></td>
                    <td className="ink2">{o.company?.legalName ?? o.companyId ?? "—"}</td>
                    <td>{supplierName(o)}</td>
                    <td className="mono">{usd(o.usdCents)}</td>
                    <td className="mono">{brl(o.totalCents)}</td>
                    <td><StatePill state={o.state} /></td>
                    <td className="muted" style={{ whiteSpace: "nowrap" }}>{dateTimeShort(o.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && (page > 1 || page * 50 < data.total) && (
          <div className="row between" style={{ padding: "12px 16px", borderTop: "1px solid var(--line)" }}>
            <button type="button" className="btn ghost xs" disabled={page <= 1} onClick={() => { const n = new URLSearchParams(sp); n.set("page", String(page - 1)); setSp(n); }}>{t.common.back}</button>
            <span className="muted small">{fmt(t.common.page, { p: page })}</span>
            <button type="button" className="btn ghost xs" disabled={page * 50 >= data.total} onClick={() => { const n = new URLSearchParams(sp); n.set("page", String(page + 1)); setSp(n); }}>{t.common.continue}</button>
          </div>
        )}
      </div>
    </TocaPage>
  );
}
